import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  fetchStudentById,
  clearSelectedEntity,
  selectSelectedStudent,
  selectStudentDetailStatus,
  selectStudentDetailError,
} from '../../features/students/studentSlice';
import { selectUserInformations } from '../../features/authSlice';
import { InputField } from '../../components/reusable/InputField';
import ToggleSwitch from '../../components/reusable/ToggleSwitch';
import Datepicker from 'react-tailwindcss-datepicker';
import UserItems from '../../components/reusable/UserItems';
import studentFields from '../../config/studentFields';
import { showToast } from '../../utils/utils';
import { formatPhoneNumberForDisplay } from '../../utils/phoneUtils';
import Loader from '../../components/Live/Loader';
import { FiEdit, FiArrowLeft } from 'react-icons/fi';
import StudentHistory from '../../components/Student/StudentHistory';

/**
 * Preview Student Page
 * Full page read-only view of student details
 */
const PreviewStudentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);

  const selectedStudent = useSelector(selectSelectedStudent);
  const detailStatus = useSelector(selectStudentDetailStatus);
  const detailError = useSelector(selectStudentDetailError);

  useEffect(() => {
    if (id && user?.token) {
      dispatch(fetchStudentById({ token: user.token, studentId: id }));
    }

    return () => {
      dispatch(clearSelectedEntity());
    };
  }, [id, user?.token, dispatch]);

  useEffect(() => {
    if (detailStatus === 'failed' && detailError) {
      showToast('Error', detailError, 'error');
      navigate('/dashboard/students', { replace: true });
    }
  }, [detailStatus, detailError, navigate]);

  const loading = detailStatus === 'loading';

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader />
      </div>
    );
  }

  if (detailStatus === 'failed') {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 dark:text-red-400 mb-4">{detailError}</p>
          <button
            onClick={() => navigate('/dashboard/students')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Students
          </button>
        </div>
      </div>
    );
  }

  if (!selectedStudent) {
    return null;
  }

  const textFields = studentFields.filter(field => 
    (field.type === 'text' || field.type === 'number') && 
    field.name !== 'user_uuid'
  );
  const datepickerField = studentFields.find(field => field.type === 'datepicker');
  const toggleField = studentFields.find(field => field.type === 'toggle');
  const userField = studentFields.find(field => field.name === 'user_uuid');

  const containerVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        staggerChildren: 0.1,
        when: "beforeChildren"
      }
    },
    exit: { opacity: 0, y: -20 }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-4">
            <div className="flex items-center gap-4">
              <Link
                to="/dashboard/students"
                className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800"
              >
                <FiArrowLeft className="w-5 h-5" />
              </Link>
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white capitalize">
                  Student Details
                </h1>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  View student information
                </p>
              </div>
            </div>
            <Link
              to={`/dashboard/students/${id}/edit`}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <FiEdit className="w-4 h-4" />
              <span>Edit Student</span>
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial="hidden"
          animate="visible"
          exit="exit"
          variants={containerVariants}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {textFields.map((field, index) => {
              // Format mobile number for display
              const displayValue = field.name === 'mobile' 
                ? (selectedStudent[field.name] ? formatPhoneNumberForDisplay(selectedStudent[field.name]) : '')
                : (selectedStudent[field.name] || '');
              
              return (
                <motion.div
                  key={field.name}
                  variants={itemVariants}
                  transition={{ delay: index * 0.05 }}
                >
                  <InputField
                    label={field.label}
                    name={field.name}
                    placeholder={field.placeholder}
                    value={displayValue}
                    disabled={true}
                  />
                </motion.div>
              );
            })}

            {userField && (
              <motion.div variants={itemVariants}>
                <UserItems 
                  field={userField} 
                  formData={selectedStudent} 
                  type="preview" 
                  disabled={true} 
                  accountType="student"
                />
              </motion.div>
            )}

            {datepickerField && selectedStudent[datepickerField.name] && (
              <motion.div
                key={datepickerField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {datepickerField.label}
                </label>
                <Datepicker
                  inputClassName="rounded-lg border-2 w-full p-2 bg-gray-50 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  readOnly={true}
                  asSingle={true}
                  useRange={false}
                  primaryColor='blue'
                  disabled={true}
                  value={{
                    startDate: new Date(selectedStudent[datepickerField.name]),
                    endDate: new Date(selectedStudent[datepickerField.name])
                  }}
                  toggleClassName="absolute bg-blue-600 rounded-r-lg text-white right-0 h-full px-3 text-gray-400 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </motion.div>
            )}

            {toggleField && (
              <motion.div
                key={toggleField.name}
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                <ToggleSwitch
                  label={toggleField.label}
                  checked={selectedStudent[toggleField.name] === 'active' || !!selectedStudent[toggleField.name]}
                  className="space-x-2"
                  disabled={true}
                />
              </motion.div>
            )}
          </div>
        </motion.div>

        {/* Student History Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 mt-6"
        >
          <StudentHistory studentData={selectedStudent} loading={loading} />
        </motion.div>
      </div>
    </div>
  );
};

export default PreviewStudentPage;
